import { useMatch, NavLink } from "react-router-dom";
import type { Immutable } from "immer";
import { Box, Divider, ListItemButton, ListItemIcon, ListItemText, Typography, useTheme } from "@mui/material";
import type { FunctionComponent } from "react";
import { useSelector } from "react-redux";

import type { RootState } from "../store/state/rootReducer";
import useMixPanel from "../lib/mixpanel";

import { makeStyles, useScreenSize } from "./theme/theme";
import { iconMap } from "./NewDrawerMenu";
import { DrawerMenuSubItem } from "./NewDrawerMenuSubItem";
import type { INavItem } from "./interfaces/interfaces";
import { BadgeBetaFeature } from "./BadgeBetaFeature";

// Matches the sidebar drawer's own width transition (NewPageLayout.tsx) so every
// piece of content fades/collapses in the same window instead of at a mismatched pace.
const SIDEBAR_DURATION_MS = 300;

const useMenuItemStyles = makeStyles<{ isSelected: boolean; isExpanded: boolean }>()(
  (theme, { isSelected, isExpanded }) => {
    // ease-out (decelerate) when settling into the expanded state, ease-in
    // (accelerate) when collapsing away — matches the drawer's own width transition.
    const SIDEBAR_EASING = isExpanded ? theme.transitions.easing.easeOut : theme.transitions.easing.easeIn;

    return {
      muiList: {
        padding: "0 !important",
        listStyle: "none",
      },
      links: {
        textDecoration: "none",
      },
      listItem: {
        // color: "inherit !important",
        color: "#464C53",
        paddingleft: theme.spacing(2),
        paddingtop: theme.spacing(1.2),
        paddingRight: "8px",
        paddingLeft: "8px",
        borderRadius: "8px",
        paddingbottom: theme.spacing(1.2),
        [theme.breakpoints.down("sm")]: {
          paddingleft: +theme.spacing(2) * theme.mobileScalingFactor * (1 - theme.iconScalingFactor),
          paddingtop: +theme.spacing(1.2) * theme.mobileScalingFactor * (1 - theme.iconScalingFactor),
          paddingbottom: +theme.spacing(1.2) * theme.mobileScalingFactor * (1 - theme.iconScalingFactor),
        },
        "&.Mui-selected": {
          backgroundColor: "#DEF0EE",
        },
        "&:hover": {
          color: "#464C53",
        },
      },
      iconRoot: {
        justifyContent: "center",
        minWidth: 0,
        width: isExpanded ? "24px" : "100%",
        marginRight: isExpanded ? "4px" : 0,
        color: isSelected && `#187266 !important`,
        transition: `width ${SIDEBAR_DURATION_MS}ms ${SIDEBAR_EASING}, margin-right ${SIDEBAR_DURATION_MS}ms ${SIDEBAR_EASING}`,
      },
      title: {
        color: isSelected ? `#187266 !important` : "#464C53",
        fontWeight: 600,
        fontSize: "14px",
        lineHeight: "20px",
        margin: 0,
      },
      sectionTitle: {
        color: "#606971",
        fontSize: "12px",
        fontWeight: 500,
        lineHeight: "16px",
        textTransform: "uppercase",
      },
      sectionDivider: {
        borderColor: "#D9DCDE",
        margin: "20px 16px 24px",
      },
      // CSS grid 1fr/0fr trick: unlike max-height, this collapses exactly the real
      // content height with no "dead" range at the start/end of the transition.
      // Opacity lives on the inner box with a shorter duration than the height, so
      // the fade is visibly done before the row finishes collapsing to nothing —
      // otherwise the height reaching 0 hides the content before the fade reads.
      sectionCollapse: {
        display: "grid",
        gridTemplateRows: isExpanded ? "1fr" : "0fr",
        transition: `grid-template-rows ${SIDEBAR_DURATION_MS}ms ${SIDEBAR_EASING}`,
      },
      sectionCollapseInner: {
        overflow: "hidden",
        minHeight: 0,
        opacity: isExpanded ? 1 : 0,
        transition: `opacity ${SIDEBAR_DURATION_MS / 2}ms ${SIDEBAR_EASING}`,
      },
      labelWrap: {
        display: "flex",
        alignItems: "center",
        columnGap: "6px",
        overflow: "hidden",
        whiteSpace: "nowrap",
        opacity: isExpanded ? 1 : 0,
        transition: `opacity ${SIDEBAR_DURATION_MS}ms ${SIDEBAR_EASING}`,
      },
    };
  }
);

function renderIcon(Icon: FunctionComponent) {
  return <Icon />;
}

export interface IDrawerMenuItemProps {
  item: Immutable<INavItem>;
  index: number;
  isExpanded: boolean;
  idMap: RootState["frame"]["nav"]["itemsById"];
  setIsExpanded: (expanded: boolean) => void;
  alwaysShowChildren: boolean;
}

const STAGGER_STEP_MS = 12;
const STAGGER_MAX_STEPS = 6;

export function DrawerMenuItem({
  item,
  index,
  isExpanded,
  setIsExpanded,
  idMap,
  alwaysShowChildren,
}: IDrawerMenuItemProps) {
  const newFeatureFlag = useSelector((state: RootState) => !!state.featureFlags?.flags?.newFeatureUi);
  const theme = useTheme();
  const { isSmall } = useScreenSize();

  const match = useMatch(`${item.href}/*`);

  const isSelected = !!match;

  const { classes } = useMenuItemStyles({ isSelected, isExpanded });
  const staggerDelay = `${Math.min(index, STAGGER_MAX_STEPS) * STAGGER_STEP_MS}ms`;
  const sidebarEasing = isExpanded ? theme.transitions.easing.easeOut : theme.transitions.easing.easeIn;
  if (item.hidden) return <></>;
  return (
    <li className={classes.muiList}>
      {(item.sectionDivider || item.sectionTitle) && (
        <Box className={classes.sectionCollapse} style={{ transitionDelay: staggerDelay }}>
          <Box className={classes.sectionCollapseInner} style={{ transitionDelay: staggerDelay }}>
            {item.sectionDivider && <Divider className={classes.sectionDivider} />}
            {item.sectionTitle && (
              <Box sx={{ px: "16px", mb: "8px", mt: item.sectionDivider ? 0 : "18px" }}>
                <Typography className={classes.sectionTitle}>{item.sectionTitle}</Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}
      <Box
        sx={{
          paddingRight: isExpanded ? "16px" : "6px",
          paddingLeft: isExpanded ? "16px" : "6px",
          transition: `padding ${SIDEBAR_DURATION_MS}ms ${sidebarEasing}`,
        }}
      >
        <ListItemButton
          component={NavLink as any}
          key={item.href}
          to={item.href}
          disabled={item.enabled === false}
          onClick={() => {
            if (item.children.length < 1 && isSmall) {
              setIsExpanded(false);
            }
            useMixPanel(`navigate_to_${item.title}`);
          }}
          className={classes.listItem}
          disableGutters
          selected={!!match}
        >
          <>
            {item.icon && iconMap[item.icon] ? (
              <ListItemIcon
                classes={{
                  root: classes.iconRoot,
                }}
              >
                {renderIcon(iconMap[item.icon])}
              </ListItemIcon>
            ) : !item.enabled ? (
              iconMap.blocked
            ) : null}
            {!isSmall && (
              <Box className={classes.labelWrap} style={{ transitionDelay: staggerDelay }}>
                <ListItemText sx={{ margin: 0 }} primary={<span className={classes.title}>{item.title}</span>} />
                {newFeatureFlag && item.beta && <BadgeBetaFeature />}
              </Box>
            )}
          </>
          {/* {isSmall && !isExpanded && (item.icon || !item.enabled) ? null : (
            <ListItemText sx={{ margin: 0 }} primary={<span className={classes.title}>{item.title}</span>} />
          )} */}
        </ListItemButton>
        {isExpanded && item.children.length > 0 && (!!match || alwaysShowChildren) && (
          <div style={{ borderLeft: `4px solid ${theme.palette.primary.dark}` }}>
            <DrawerMenuSubItem ids={item.children} idMap={idMap} setIsExpanded={setIsExpanded} />
          </div>
        )}
      </Box>
    </li>
  );
}
